import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Archive, Paperclip } from "lucide-react"
import { PostfachMailModal } from "@/components/portal/PostfachMailModal"
import { postfachParams } from "@/components/portal/postfachParams"
import { cleanSender, extractEmail, fmtDateLong } from "./format"

// Das Lese-Fenster (PostfachMailModal) liegt in components/portal — es wird
// auch für geleerte Portal-Mails gebraucht (Dossier, Posteingang, Mail-Detail).

/**
 * Postfach-Suche: ältere Mails live aus dem IMAP-Postfach (DSGVO, 02.10.2026).
 *
 * Nach 90 Tagen leert der Löschjob den Inhalt einer Mail im Portal — die
 * normale Suche findet sie dann nicht mehr. Die Originale liegen aber weiter im
 * Postfach des Kunden. Dieser Abschnitt sucht dort live und zeigt die Treffer
 * unter den normalen Ergebnissen; geöffnet wird ebenfalls live.
 *
 * 🔴 Nur lesen. Keine HERO-, Plan- oder Buchungsaktionen — die Mail ist nicht
 * im Portal und soll es auch nicht werden.
 *
 * 🔴 Ein Fehler wird als Fehler gezeigt, nie als „keine Treffer" (stumme
 * Nulltreffer). `verfuegbar: false` (kein Postfach angebunden) → nichts.
 */

export type PostfachTrefferZeile = {
  tenant_slug: string | null
  postfach: string | null
  postfach_label?: string | null
  folder: string
  folder_label: string
  uid: string
  message_id: string | null
  date: string | null
  subject: string
  sender: string
  to: string
  has_attachments: boolean
}

export type PostfachSuchErgebnis = {
  verfuegbar: boolean
  ok?: boolean
  fehler?: string | null
  unvollstaendig?: boolean
  ordner_durchsucht?: number
  ordner_gesamt?: number
  postfaecher_gesamt?: number
  postfaecher_nicht_erreicht?: string[]
  vereinfacht?: boolean
  ohne_text?: boolean
  mehr?: boolean
  treffer?: PostfachTrefferZeile[]
  frist_tage?: number
}

/** Ab so vielen Zeichen wird das Postfach gefragt — kürzere Suchen nicht. */
export const POSTFACH_MIN_ZEICHEN = 3

const hinweisStil: React.CSSProperties = {
  padding: "6px 16px", fontSize: 12, color: "#92400e", background: "rgba(245, 158, 11, 0.08)",
}

/** Was genau fehlt — damit „nicht gefunden“ einzuordnen ist (Paul
 *  03.11.2026: „wie aussagekräftig ist das?“). */
export function unvollstaendigText(d: PostfachSuchErgebnis): string {
  const teile: string[] = []
  const nicht = d.postfaecher_nicht_erreicht || []
  if (nicht.length) {
    teile.push(`${nicht.length === 1 ? "Postfach" : "Postfächer"} nicht erreichbar: ${nicht.join(", ")}`)
  }
  if (d.ordner_gesamt && (d.ordner_durchsucht ?? 0) < d.ordner_gesamt) {
    teile.push(`${d.ordner_durchsucht ?? 0} von ${d.ordner_gesamt} Ordnern durchsucht (Zeitlimit)`)
  }
  if (!teile.length) teile.push("Nicht alle Ordner durchsucht (Zeitlimit oder Ordner nicht lesbar)")
  return `${teile.join(" · ")} — es kann weitere Treffer geben.`
}

export function PostfachTreffer({ tenant, postfach, q }: { tenant: string; postfach?: string; q: string }) {
  const [offen, setOffen] = useState<PostfachTrefferZeile | null>(null)
  const aktiv = q.trim().length >= POSTFACH_MIN_ZEICHEN
  const suche = useQuery<PostfachSuchErgebnis>({
    queryKey: ["postfach-suche", tenant, postfach || "", q.trim()],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/postfach-suche?${postfachParams({ q: q.trim(), tenant, postfach })}`,
        { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    enabled: aktiv,
    // Gleiche Suche nicht sofort wieder ans Postfach (das Backend cacht auch).
    staleTime: 60_000,
    retry: false,
  })

  if (!aktiv) return null
  const d = suche.data
  if (d && !d.verfuegbar) return null

  const treffer = d?.treffer || []
  return (
    <div data-testid="postfach-suche" style={{ borderTop: "1px solid var(--border-color)", marginTop: 4 }}>
      <div className="ev2-day-header" style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <Archive size={12} />
        Ältere Mails aus dem Postfach
        {d?.frist_tage ? <span style={{ fontWeight: 400, opacity: 0.75 }}>· älter als {d.frist_tage} Tage</span> : null}
      </div>
      {suche.isLoading && (
        <div className="ev2-empty" data-testid="postfach-suche-laedt">Suche im Postfach …</div>
      )}
      {(suche.isError || (d && d.ok === false)) && (
        <div className="ev2-empty" style={{ color: "#b91c1c" }}>
          Postfach-Suche fehlgeschlagen{d?.fehler ? `: ${d.fehler}` : ""} — das heißt nicht, dass es keine älteren Treffer gibt.
        </div>
      )}
      {d?.ok && d.unvollstaendig && (
        <div style={hinweisStil} data-testid="postfach-suche-unvollstaendig">
          {unvollstaendigText(d)}
        </div>
      )}
      {d?.ok && d.mehr && (
        <div style={hinweisStil}>Mehr Treffer als angezeigt — Suche verfeinern, um ältere zu sehen.</div>
      )}
      {d?.ok && d.ohne_text && (
        <div style={hinweisStil}>Dieses Postfach durchsucht nur Betreff, Absender und Empfänger — nicht den Mailtext.</div>
      )}
      {d?.ok && d.vereinfacht && (
        <div style={hinweisStil}>Das Postfach kann nicht nach Umlauten suchen — die Suche wurde vereinfacht.</div>
      )}
      {d?.ok && treffer.length === 0 && (
        <div className="ev2-empty">Keine älteren Mails im Postfach gefunden.</div>
      )}
      {treffer.map((t) => {
        // Paul 03.11.2026: in der schmalen Spalte war nichts zu lesen
        // („<info@…“, halber Betreff). Absender mit Adresse, Betreff über zwei
        // Zeilen, Ordner und Postfach klein darunter.
        const adresse = extractEmail(t.sender)
        const name = cleanSender(t.sender).replace(/^<|>$/g, "")
        const zeigeName = name && name.toLowerCase() !== adresse.toLowerCase() ? name : ""
        return (
          <button
            key={`${t.tenant_slug}|${t.postfach}|${t.folder}|${t.uid}`}
            type="button"
            data-testid="postfach-treffer"
            className="pt-zeile"
            onClick={() => setOffen(t)}
          >
            <span className="pt-kopf">
              <b data-pii>{zeigeName || adresse || "(ohne Absender)"}</b>
              <span className="pt-datum">{fmtDateLong(t.date)}</span>
            </span>
            {zeigeName && adresse && <span className="pt-adresse" data-pii>{adresse}</span>}
            <span className="pt-betreff" data-pii>
              {t.has_attachments && <Paperclip size={12} aria-label="Anhang" />}
              {t.subject || "(kein Betreff)"}
            </span>
            <span className="pt-ort">
              aus dem Postfach · {t.folder_label}{t.postfach_label ? ` · ${t.postfach_label}` : ""}
            </span>
          </button>
        )
      })}
      {offen && (
        <PostfachMailModal
          tenant={offen.tenant_slug || tenant}
          postfach={offen.postfach}
          folder={offen.folder}
          uid={offen.uid}
          onClose={() => setOffen(null)}
        />
      )}
    </div>
  )
}
